import React from 'react'
import useOfferTags from 'hooks/Offers/useOfferTags'
import useDestinationTags from 'hooks/Search/useDestinationTags'
import { ContentTagPage } from './contentTagTypes'
import ContentTagBannerItem from './ContentTagBannerItem'
import { MessageBannerKind } from 'LuxKit/Banners/MessageBanner'

type Props = {
  /**
   * Kind for MessageBanner
   * @default 'promotional-secondary'
   */
  kind?: MessageBannerKind
  /**
   * The page context for generating the content tag slug.
   * - 'offer' generates slugs like 'offer-banner-<tag_name>'
   * - 'search' generates slugs like 'search-banner-<tag_name>'
   */
  page: ContentTagPage
} & ({
  /**
   * The offer ID to fetch tags from. Use this for offer pages.
   */
  offerId: string
  destinationId?: undefined
} | {
  offerId?: undefined
  /**
   * The destination ID to fetch tags from. Use this for search pages.
   */
  destinationId: string
})

/**
 * Displays a promotional banner based on offer or destination tags.
 * Takes an offer or destinationId, finds matching tags, and renders a banner for the first match.
 */
function ContentTagBanner({ offerId, destinationId, kind = 'promotional-primary', page }: Props) {
  const { tags: offerTags } = useOfferTags(offerId)
  const { tags: destinationTags } = useDestinationTags(destinationId, {
    disabled: !!offerId,
  })

  // Use offer tags if available, otherwise use destination tags
  const tags = offerId ? offerTags : destinationTags

  // TODO: JH - For now we expect to only handle one tag per banner. If we need to handle multiple tags, we can change this to a loop.
  const firstTag = tags[0]

  if (!firstTag?.name) {
    return null
  }
  // No normalization of tag name here, it should be the same as the same as tag name in admin portal
  const bannerSlug = `${page}-banner-${firstTag.name}`

  return <ContentTagBannerItem slug={bannerSlug} tagName={firstTag.name} kind={kind} page={page} />
}

export default ContentTagBanner
